/**
 * GA4 (Google Analytics 4) conversion event helpers.
 *
 * All helpers are safe to call even before gtag has loaded — they
 * check for window.gtag and silently no-op if it's unavailable.
 * This keeps the critical render path clean and never throws in
 * production for users with ad blockers or privacy extensions.
 */

const GA4_ID = "G-VCH0Y2GMQT";

type GtagEventParams = Record<string, string | number | boolean | undefined>;

/**
 * Fire a GA4 custom event. No-ops if gtag isn't loaded yet.
 */
export function trackEvent(
  eventName: string,
  params: GtagEventParams = {},
): void {
  if (typeof window === "undefined") return;
  const gtag = (window as any).gtag;
  if (typeof gtag !== "function") return;
  gtag("event", eventName, params);
}

/**
 * Fire a `generate_lead` conversion when a visitor submits any lead form
 * (consultation, insurance verification, webinar registration, etc.).
 *
 * @param formId   – the form identifier (e.g. "consultation-form")
 * @param formName – human-readable form name for GA4 reporting
 */
export function trackLead(formId: string, formName: string): void {
  trackEvent("generate_lead", {
    form_id: formId,
    form_name: formName,
    engagement_time_msec: 100,
  });
}

/**
 * Fire a `phone_click` event when a visitor clicks any `tel:9548279793`
 * link. This lets you see which pages drive phone calls in GA4.
 *
 * @param pageLocation – the current page path (defaults to window.location.pathname)
 */
export function trackPhoneClick(pageLocation?: string): void {
  trackEvent("phone_click", {
    phone_number: "954-827-9793",
    page_location:
      pageLocation ??
      (typeof window !== "undefined" ? window.location.pathname : "/"),
  });
}

/**
 * Install a global delegated click listener for all `tel:` links so
 * phone-call conversions are tracked site-wide without needing to
 * modify every individual <a href="tel:..."> element.
 *
 * Call once from the app root (e.g. App.tsx useEffect).
 */
export function installPhoneClickTracking(): () => void {
  if (typeof window === "undefined") return () => {};

  const handler = (e: MouseEvent) => {
    const target = (e.target as HTMLElement)?.closest?.('a[href^="tel:"]');
    if (!target) return;
    const href = target.getAttribute("href") || "";
    const phone = href.replace("tel:", "").replace(/[^0-9]/g, "");
    trackEvent("phone_click", {
      phone_number: phone,
      page_location: window.location.pathname,
      link_text: target.textContent?.trim() || "",
    });
  };

  document.addEventListener("click", handler, true);
  return () => document.removeEventListener("click", handler, true);
}

export { GA4_ID };
